import React, {useEffect, useRef, useState} from 'react';
import CFAdminDataTable from "../../components/CFAdminDataTable";
import CFAdminModal from "../../components/CFAdminModal";
import './CFAdminFootnotes.scss'
import CFAdminFootnotesForm from './CFAdminFootnotesForm';
import FootnotesService from '../../services/FootnotesService';
import CFAdminNotification from '../../components/CFAdminNotification';
import {Footnotes, FootnotesLanguage} from "./types";
import {CountryLanguageEnum} from "../../constants/CountryLanguageEnum";
import {ActionMode} from "../../constants/ActionMode";
import ActionButtons from "../../util/renderActionButtons";

const FootnotesActionCell = (props: any, handleChange: Function, handleDelete: Function) => {
    return <ActionButtons props={props} handleChange={handleChange} handleDelete={handleDelete}/>
}

const FootnotesCodeCell = ({value}: any) => <div className="cell-max-row-height">{value}</div>;

const FootnotesDescriptionCell = ({value}: any) => <div className="cell-left-justified">{value}</div>;

function CFAdminFootnotes() {
    const childCompRef = useRef<any>();
    const [data, setData] = useState([] as Footnotes[]);
    const [isDelete, setIsDelete] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [actionText, setActionText] = useState("Add FootNotes");
    const [initialValues, setInitialValues] = useState(
        {
            footNotesId: 0, mode: ActionMode.Add, footNotesCode: "",
            adminDesc: "", adminDescId: 0,
            adminUsSpaDesc: "", adminUsSpaDescId: 0,
            adminCanEngDesc: "", adminCanEngDescId: 0,
            adminCanFrDesc: "", adminCanFrDescId: 0, modified: "", userKey: ""
        });

    const columns = [
        {
            Header: "FootNotes Code",
            accessor: 'footNotesCode',
            Cell: FootnotesCodeCell
        },
        {
            Header: "Description",
            accessor: (row: Footnotes) => {
                const footnotesLanguage: FootnotesLanguage | undefined = row.footNotesLanguageList.find(opLang => opLang.countryLanguageId === CountryLanguageEnum.US_ENG);
                return footnotesLanguage ? footnotesLanguage.description : '';
            },
            Cell: FootnotesDescriptionCell
        },
        {
            Header: "Last Modified By",
            accessor: 'userKey'
        },
        {
            Header: "Last Modified Date",
            accessor: 'modified'
        },
        {
            Header: "Actions",
            Cell: (props: any) => FootnotesActionCell(props, handleChange, handleDelete),
        }
    ];

    const handleChange = (event: any) => {
        const footnotesLanguageUsEng: FootnotesLanguage | undefined = event.originalRow.footNotesLanguageList.find((opLang: {
            countryLanguageId: CountryLanguageEnum;
        }) => opLang.countryLanguageId === CountryLanguageEnum.US_ENG);
        const footnotesLanguageUsSpa: FootnotesLanguage | undefined = event.originalRow.footNotesLanguageList.find((opLang: {
            countryLanguageId: CountryLanguageEnum;
        }) => opLang.countryLanguageId === CountryLanguageEnum.US_SPA);
        const footnotesLanguageCanEng: FootnotesLanguage | undefined = event.originalRow.footNotesLanguageList.find((opLang: {
            countryLanguageId: CountryLanguageEnum;
        }) => opLang.countryLanguageId === CountryLanguageEnum.CAN_ENG);
        const footnotesLanguageCanFra: FootnotesLanguage | undefined = event.originalRow.footNotesLanguageList.find((opLang: {
            countryLanguageId: CountryLanguageEnum;
        }) => opLang.countryLanguageId === CountryLanguageEnum.CAN_FRA);

        setInitialValues({
            ...initialValues,
            mode: ActionMode.Edit,
            footNotesId: event.originalRow.footNotesId,
            footNotesCode: event.originalRow.footNotesCode,
            adminDesc: footnotesLanguageUsEng ? footnotesLanguageUsEng.description : '',
            adminDescId: footnotesLanguageUsEng ? footnotesLanguageUsEng.languageTextId : 0,
            adminUsSpaDesc: footnotesLanguageUsSpa ? footnotesLanguageUsSpa.description : '',
            adminUsSpaDescId: footnotesLanguageUsSpa ? footnotesLanguageUsSpa.languageTextId : 0,
            adminCanEngDesc: footnotesLanguageCanEng ? footnotesLanguageCanEng.description : '',
            adminCanEngDescId: footnotesLanguageCanEng ? footnotesLanguageCanEng.languageTextId : 0,
            adminCanFrDesc: footnotesLanguageCanFra ? footnotesLanguageCanFra.description : '',
            adminCanFrDescId: footnotesLanguageCanFra ? footnotesLanguageCanFra.languageTextId : 0,
            modified: '',
            userKey: ''
        });
        setIsOpen(!isOpen);
        setActionText("Edit FootNotes");
    }

    const handleDelete = (event: any) => {
        setActionText("Delete FootNotes");
        setIsDelete(true);
        handleChange(event);
    }

    useEffect(() => {
        retrieveFootnotes();
    }, []);

    const retrieveFootnotes = async () => {
        FootnotesService.getAll()
            .then(response => {
                const footnotes = response?.data?.footNotesList ?? [] as Footnotes[];
                const sortedFootnotes = Array.isArray(footnotes)
                    ? [...footnotes].sort((a: Footnotes, b: Footnotes) =>
                        (a.footNotesCode || '').localeCompare(b.footNotesCode || '', undefined, {sensitivity: 'base'})
                    )
                    : [];
                setData(sortedFootnotes);
            })
            .catch(e => {
                console.error(e);
            });
    };

    const confirmSuccessMessage = async (value: any) => {
        childCompRef.current.successMessage(value);
    };
    const confirmErrorMessage = async (value: any) => {
        childCompRef.current.failureMessage(value);
    };


    const toggleModal = () => {
        setInitialValues({
            ...initialValues,
            footNotesId: 0, mode: ActionMode.Add, footNotesCode: "",
            adminDesc: "", adminDescId: 0,
            adminUsSpaDesc: "", adminUsSpaDescId: 0,
            adminCanEngDesc: "", adminCanEngDescId: 0,
            adminCanFrDesc: "", adminCanFrDescId: 0
        });
        setIsOpen(!isOpen);
        setActionText("Add FootNotes");
        setIsDelete(false);
    }

    return (

        <div className="footnotes-component">
            <h2 className="fomo-type--title">FootNotes List</h2>
            <CFAdminModal
                isOpen={isOpen}
                toggle={toggleModal}
                isAdd={true}
                title={actionText}
            >
                <CFAdminFootnotesForm {...initialValues}
                                      isDelete={isDelete}
                                      toggleModal={toggleModal}
                                      refreshData={retrieveFootnotes}
                                      successMessage={confirmSuccessMessage}
                                      errorMessage={confirmErrorMessage}/>
            </CFAdminModal>

            <CFAdminDataTable
                columns={columns}
                data={data}
                toggleModal={toggleModal}
                textModal={'Add FootNotes'}
            />
            <CFAdminNotification ref={childCompRef}/>
        </div>
    );
}

export default CFAdminFootnotes;